import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useRef, useState, Component } from 'react';
import Dashboard from './components/dashboard/Dashboard.js';
import CoursePanel from './components/content/CoursePanel.js'; 
import Register from './auth/Register';
import Login from './auth/Login';
import SideNav from './components/dashboard/SideNav.js';
import { SidebarProvider } from './context/SidebarContext';
import SideNavDesktop from './components/dashboard/SideNavDesktop';
import Settings from './components/dashboard/Settings.js';
import Pricing from './components/payment/Plan.js';
import ProtectedRoute from './utils/protectedRoute';
import { useAuth } from './context/AuthContext';
import posthog from 'posthog-js';

// Debug flag (add ?debug_analytics=1 or localStorage analytics_debug=1)
const ANALYTICS_DEBUG =
  typeof window !== 'undefined' &&
  (/\bdebug_analytics=1\b/.test(window.location.search) ||
    window.localStorage?.getItem('analytics_debug') === '1');

// Minimal error boundary to avoid blank screens on render errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    try { console.error('App ErrorBoundary caught:', error, info); } catch {}
  }
  reset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };
  render() {
    if (this.state.hasError) {
      const message = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
      return (
        <div style={{ position:'fixed', inset:0, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}>
          <div style={{ maxWidth:520, padding:16, textAlign:'center', color:'#111' }}>
            <div style={{ fontSize:18, fontWeight:600, marginBottom:8 }}>Something went wrong</div>
            <div style={{ fontSize:12, color:'#666', whiteSpace:'pre-wrap', maxHeight:120, overflow:'auto' }}>{message}</div>
            <div style={{ marginTop:16, display:'flex', gap:8, justifyContent:'center' }}>
              <button onClick={this.reset} style={{ padding:'8px 12px', background:'#4f46e5', color:'#fff', borderRadius:8 }}>Try again</button>
              <button onClick={() => window.location.assign('/')} style={{ padding:'8px 12px', background:'#e5e7eb', borderRadius:8 }}>Go Home</button>
              <button onClick={() => window.location.reload()} style={{ padding:'8px 12px', background:'#e5e7eb', borderRadius:8 }}>Reload</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const GTAG_ID = 'G-7W5TBMHH66';

// PostHog init
if (typeof window !== 'undefined' && !window.__POSTHOG_INIT__) {
  posthog.init('phc_3KyWlDvC5FFvlScG71mFMkRIibPPcK6NfPotUVqDWt0', {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
  });
  window.__POSTHOG_INIT__ = true;
}

function App() {
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isPosthogReady, setIsPosthogReady] = useState(false);
  const loaderTimeoutRef = useRef();

  // Helper to show the global loader for a minimum duration without flicker
  const triggerSoftLoader = (duration = 700) => {
    try { clearTimeout(loaderTimeoutRef.current); } catch {}
    setLoading(true);
    loaderTimeoutRef.current = setTimeout(() => setLoading(false), duration);
  };

  useEffect(() => {
    // Start loader on every path change
    triggerSoftLoader(700);
    return () => {
      clearTimeout(loaderTimeoutRef.current);
    };
  }, [location.pathname]);

  useEffect(() => {
    // When returning to the tab after a while, show a brief loader to mask auth/token refresh and Firestore wake-up
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        triggerSoftLoader(600);
      }
    };
    // Handle back/forward cache restores (Safari/Chrome bfcache)
    const onPageShow = (e) => {
      if (e.persisted) {
        triggerSoftLoader(600);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };
    const scriptId = `gtag-${GTAG_ID}`;
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
      document.head.appendChild(script);
    }
    window.gtag('js', new Date());
    window.gtag('config', GTAG_ID);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('config', GTAG_ID, {
      page_path: `${location.pathname}${location.search}`,
      page_location: window.location.href,
    });
  }, [location.pathname, location.search]);

  // PostHog readiness
  useEffect(() => {
    setIsPosthogReady(true);
    ANALYTICS_DEBUG && console.info('[PostHog] ready');
  }, []);

  // PostHog pageview
  useEffect(() => {
    if (!isPosthogReady) return;
    try {
      posthog?.capture?.('$pageview', {
        path: location.pathname,
        search: location.search || undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      });
      ANALYTICS_DEBUG && console.info('[PostHog] $pageview', location.pathname, location.search);
    } catch (e) {
      ANALYTICS_DEBUG && console.warn('[PostHog] pageview error', e);
    }
  }, [isPosthogReady, location.pathname, location.search]);

  // PostHog identify
  useEffect(() => {
    if (!isPosthogReady) return;
    try {
      if (user?.uid) {
        const displayName = (user.displayName || `${user.firstName || ''} ${user.lastName || ''}`).trim() || undefined;
        posthog.identify(user.uid, {
          email: user.email || undefined,
          name: displayName,
        });
        ANALYTICS_DEBUG && console.info('[PostHog] identify', user.uid);
      } else {
        posthog.reset?.();
        ANALYTICS_DEBUG && console.info('[PostHog] reset (no user)');
      }
    } catch (e) {
      ANALYTICS_DEBUG && console.warn('[PostHog] identify error', e);
    }
  }, [isPosthogReady, user?.uid, user?.email, user?.displayName, user?.firstName, user?.lastName]);

  return (
    <ErrorBoundary key={location.pathname}>
      <SidebarProvider>
        {/* Global Loader */}
        {loading && (
          <div className="global-loader" role="status" aria-live="polite" aria-busy="true">
            <div className="spinner" />
          </div>
        )}
        {/* Minimal inline styles for the loader */}
        <style>
          {`
            .global-loader {
              position: fixed; inset: 0;
              display: flex; align-items: center; justify-content: center;
              background: rgba(255,255,255,0.85);
              backdrop-filter: blur(1px);
              z-index: 9999;
            }
            .spinner {
              width: 40px; height: 40px;
              border: 3px solid rgba(0,0,0,0.1);
              border-top-color: #072bb9ff;
              border-radius: 50%;
              animation: spin .8s linear infinite;
            }
            @keyframes spin { to { transform: rotate(360deg); } }
          `}
        </style>

        <div className="app">
          <SideNav />
          <SideNavDesktop />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/register"
              element={
                <ProtectedRoute>
                  <Register />
                </ProtectedRoute>
              }
            />
            <Route
              path="/login"
              element={
                <ProtectedRoute>
                  <Login />
                </ProtectedRoute>
              }
            />
            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <ProtectedRoute>
                  <Pricing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="c/:contentId"
              element={
                <ProtectedRoute>
                  <CoursePanel />
                </ProtectedRoute>
              }
            />
            {/* Catch-all: redirect unknown routes to homepage */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </SidebarProvider>
    </ErrorBoundary>
  );
}

export default App;
